<template>
  <div class="bg-blue-600 text-white select-none h-[calc(100vh-5rem)] overflow-y-auto overflow-x-hidden shrink-0" :class="[{ 'min-w-72': modelValue }]">
    <div v-for="(menuItem, index) in sortedMenuList" :key="index" class="bg-blue-600">
      <div class="py-1" :class="{ 'bg-blue-700': menuItem.expanded }">
        <a
          :href="getItemHref(menuItem)"
          @click="menuItemClicked($event, menuItem, sortedMenuList)"
          class="block hover:bg-white hover:text-blue-600 px-2 py-2 mx-1 rounded-lg cursor-pointer">
          <span class="menu-text flex items-center">
            <i v-if="menuItem.icon" :class="menuItem.icon" class="text-xl mx-2 p-1 w-8 text-center"></i>
            <span class="grow" v-if="props.modelValue">
              {{ menuItem.name }}
            </span>
            <span v-if="menuItem.badge && menuItem.badge > 0" class="bg-white text-blue-600 text-xs font-bold rounded-full px-2 py-1 ml-2">
              {{ menuItem.badge }}
            </span>
            <i
              v-if="menuItem.children && menuItem.children.length > 0 && props.modelValue"
              :class="`fa-chevron-down transition ${menuItem.expanded ? ' rotated-icon' : ''}`"
              class="fa-solid ms-2"></i>
          </span>
        </a>

        <div v-if="menuItem.expanded" class="pt-1">
          <div v-for="(subItem, idx) in menuItem.children" :key="idx">
            <div class="py-1" :class="{ 'bg-blue-800': subItem.expanded }">
              <a
                :href="getItemHref(subItem)"
                :class="menuLinkClasses(subItem)"
                @click="menuItemClicked($event, subItem, menuItem.children)"
                class="block hover:bg-white hover:text-blue-600 px-2 py-2 mx-1 rounded-lg cursor-pointer">
                <span class="menu-text flex items-center">
                  <i v-if="subItem.icon" :class="subItem.icon" class="text-xl mx-2 p-1 w-8 text-center"></i>
                  <span class="grow" v-if="props.modelValue">
                    {{ subItem.name }}
                  </span>
                  <span v-if="subItem.badge" class="bg-white text-blue-600 text-xs font-bold rounded-full px-2 py-1 ml-2">
                    {{ subItem.badge }}
                  </span>
                  <i
                    v-if="subItem.children && subItem.children.length > 0 && props.modelValue"
                    :class="`fa-chevron-down transition ${subItem.expanded ? ' rotated-icon' : ''}`"
                    class="fa-solid ms-2"></i>
                </span>
              </a>

              <div v-if="subItem.expanded" class="bg-blue-800 py-1">
                <div v-for="(childItem, childIdx) in subItem.children" :key="childIdx">
                  <a
                    :href="getItemHref(childItem)"
                    :class="menuLinkClasses(childItem)"
                    @click="menuItemClicked($event, childItem, subItem.children)"
                    class="block hover:bg-white hover:text-blue-600 px-2 py-2 mx-1 rounded-lg cursor-pointer">
                    <span class="menu-text flex items-center">
                      <i v-if="childItem.icon" :class="childItem.icon" class="text-xl mx-2 p-1 w-8 text-center"></i>
                      <span class="grow" v-if="props.modelValue">
                        {{ childItem.name }}
                      </span>
                      <span v-if="childItem.badge" class="bg-white text-blue-600 text-xs font-bold rounded-full px-2 py-1 ml-2">
                        {{ childItem.badge }}
                      </span>
                    </span>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { get } from '@/crud'
import { ref, onMounted, watch, computed } from 'vue'
import { useRouter } from 'vue-router'
import { useRoute } from 'vue-router'
import { useStore } from 'vuex'

const store = useStore()
const route = useRoute()
const router = useRouter()

const props = defineProps({
  modelValue: Boolean,
})

const emits = defineEmits(['update:modelValue'])

const menuList = ref([])

const sortedMenuList = computed(() => {
  return menuList.value
})

const badges = computed(() => {
  return store.state.badges
})

const fetchMenu = async () => {
  try {
    const response = await get('menu')
    menuList.value = response.data
  } catch (error) {
    console.error('Error fetching menu:', error)
  }
}

const fetchBadges = async () => {
  try {
    const response = await get('menu/badges')
    if (!badges.value.status) {
      store.dispatch('setBadges', response)
    }
  } catch (error) {
    console.error('Error fetching badges:', error)
  }
}

const getBadgeCount = () => {
  const rawBadges = badges.value
  if (rawBadges) {
    const badgeList = Object.keys(rawBadges)

    for (const menuItem of menuList.value) {
      for (const child of menuItem.children) {
        const menuName = child.name?.toLowerCase() || ''
        const badge = badgeList.find((key) => menuName.includes(key))
        // const badge = badgeList.find((key) => menuName.includes(key) || key.includes(menuName))

        if (badge) {
          child.badge = rawBadges[badge]
        }
      }
    }
  }
}

const menuLinkClasses = (subItem) => {
  return ''
}

const hasChildren = (menuItem) => {
  return menuItem.children && menuItem.children.length > 0
}

const isNavigable = (menuItem) => {
  return menuItem.screenRoute && menuItem.screenRoute !== 'null'
}

const isLinkItem = (menuItem) => {
  return isNavigable(menuItem) && !hasChildren(menuItem)
}

const getItemHref = (menuItem) => {
  if (!isLinkItem(menuItem)) {
    return null
  }

  return router.resolve(menuItem.screenRoute).href
}

const menuItemClicked = (event, menuItem, siblings) => {
  if (!event || event.button !== 0) {
    return
  }

  if (hasChildren(menuItem)) {
    event.preventDefault()
    siblings.forEach((item) => {
      if (item !== menuItem) {
        item.expanded = false
        closeAllChildren(item)
      }
    })
    emits('update:modelValue', true)
    menuItem.expanded = !menuItem.expanded
    return
  }

  if (!isNavigable(menuItem)) {
    event.preventDefault()
    return
  }

  // Deixa atalhos nativos (ctrl/cmd/shift/alt + click) e menu de contexto funcionarem como link comum.
  if (event.ctrlKey || event.metaKey || event.shiftKey || event.altKey) {
    return
  }

  event.preventDefault()
  goTo(menuItem)
}

const closeAllChildren = (menuItem) => {
  menuItem.children.forEach((child) => {
    child.expanded = false
    if (child.children && child.children.length > 0) {
      closeAllChildren(child)
    }
  })
}

const goTo = (item) => {
  if (isNavigable(item)) {
    router.push(item.screenRoute)
  }
}
watch(
  badges,
  async (newVal, oldVal) => {
    if (newVal !== oldVal) {
      getBadgeCount()
    }
  },
  { deep: true },
)
watch(
  route,
  async (newVal) => {
    await fetchBadges()

    getBadgeCount()
  },
  { deep: true },
)
onMounted(async () => {
  await fetchMenu()
  await fetchBadges()
})
</script>

<style>
.nav-link.active {
  background-color: #f3f3f3;
}

.nav-item > a {
  display: flex;
  align-items: center;
}

.menu-text {
  flex-grow: 1;
  display: flex;
  align-items: center;
}

.toggle-icon {
  margin-left: auto;
}
.rotated-icon {
  transform: rotate(-180deg);
  transition: transform 0.3s ease ease-out;
}
.transition {
  transition: transform 0.3s ease-out;
}
</style>
